import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import EventBus from 'lib/EventBus';
import { useGetAdminSettings } from 'queries';
import { useRecoilState, useRecoilValue } from 'recoil';

import { getPlanUnitIds } from '../../../utils';
import {
  getClientsOrEmployeesList,
  getEntityPreferenceKey,
  getMatchingPlanningEntityIdentifiers,
  getPlanningByResponseEntities,
  getPlanningEntityTypeByView,
  isEqualToSessionCorrelationToken,
  normalizePlanningData,
  refetchPlanResponsePlanningEntityIdentifiers
} from '../utils';

import { mutations } from 'RestAPIs/rapini';

import { apDebug } from '../apDebug';
import { verifyDropAfterRefresh } from '../dropVerification';
import {
  employeeEntityIdsFilterAtom,
  employeesAtom,
  kidsRatioAtom,
  planningAtom,
  planResponsePlanningEntityIdentifiersAtom,
  planUnitsAtom,
  selectedPlanningEntitiesAtom,
  View
} from '../atoms';

import { useSessionStorage } from 'usehooks-ts';
import { useSelector } from '../../../../hooks';
import { LISTVIEW_SESSION_STORAGE_KEYS } from '../ListView/types';
import {
  useConnectAppointmentGrouping,
  useConnectSchedule
} from '../mutations';
import { useGetPlanning } from '../queries';
import usePlanningEntity from './usePlanningEntity';
import useSelectedDateRange from './useSelectedDateRange';

import type {
  CommandResultResponse,
  EmployeeResponse,
  KidsRatioOverviewResponse,
  PlanAppointmentsRequest,
  PlanningEntityIdentifier,
  PlanningEntityType,
  PlanningResponse
} from 'generated';
import type { UseMutateAsyncFunction, UseMutateFunction } from 'react-query';
import type {
  CommandResult,
  ConnectAppointmentGroupingPayload,
  ConnectSchedulePayload
} from 'RestAPIs/activities';
import type {
  PlanningEntitiesWebsocketBody,
  WebsocketBody
} from 'RestAPIs/activities/types';
import type { PlanningEntity, PlanningEntityMap } from '../types';

export type UsePlanningReturn = {
  data: PlanningResponse | undefined;
  kidsRatio: KidsRatioOverviewResponse | null;
  isMyActivities?: boolean;
  planningEntities: PlanningEntityMap | null;
  setPlanningEntities: (value: any) => void;
  planAppointments: UseMutateFunction<
    CommandResultResponse,
    unknown,
    PlanAppointmentsRequest,
    unknown
  >;
  /**
   * Promise-based variant of `planAppointments`. React Query's mutation
   * observer keeps only the latest call's callbacks (it reassigns
   * `mutateOptions` and detaches the previous mutation on every `mutate`), so a
   * caller that can have two mutations in flight at once must await its own
   * promise instead of relying on per-call callbacks.
   */
  planAppointmentsAsync: UseMutateAsyncFunction<
    CommandResultResponse,
    unknown,
    PlanAppointmentsRequest,
    unknown
  >;
  connectAppointmentGrouping: UseMutateFunction<
    CommandResult,
    unknown,
    ConnectAppointmentGroupingPayload,
    unknown
  >;
  connectSchedule: UseMutateFunction<
    CommandResult,
    unknown,
    ConnectSchedulePayload,
    unknown
  >;
  startDate: string;
  endDate: string;
  currentPlanningEntityIdentifiers: PlanningEntityIdentifier[];
  /**
   * Identifies the board the user is looking at: every input that replaces the
   * whole board when it changes, and nothing a single drop touches. Lets a
   * consumer tell "this load belongs to the board on screen" from "the board
   * itself is being replaced" — see `useColumnLoading`.
   */
  boardKey: string;
  /**
   * Bumped whenever something outside the view forces a full reload — today a
   * websocket `activityPlanningUpdated`. A consumer that normally scopes its
   * loading state to the columns a drop touches can't do that for a reload it
   * never asked for, and uses this to put the global overlay back. A value that
   * changes rather than a callback to wire up, like `planResponseTimestamp`.
   */
  externalRefreshCount: number;
  isLoading: boolean;
  employees: EmployeeResponse[];
  showEvents: boolean;
};

export default function usePlanning(
  view: View,
  showEvents: boolean
): UsePlanningReturn {
  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType =
    planningEntityTypePreference[getEntityPreferenceKey(view)];

  const [kidsRatio, setKidsRatio] = useRecoilState(kidsRatioAtom);
  const [planningEntities, setPlanningEntities] = useRecoilState(planningAtom);

  const [dataAvailable, setDataAvailable] = useState(false);

  const { startDate, endDate } = useSelectedDateRange();
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);
  const [employees, setEmployees] =
    useRecoilState<EmployeeResponse[]>(employeesAtom);
  const [employeeEntityIds] = useRecoilState(employeeEntityIdsFilterAtom);
  const [
    {
      ts: planResponseTimestamp,
      planningEntityIdentifiers: planResponsePlanningEntityIdentifiers
    },
    setPlanResponsePlanningEntityIdentifiers
  ] = useRecoilState(planResponsePlanningEntityIdentifiersAtom);
  const [selectedPlanningEntities] = useRecoilState(
    selectedPlanningEntitiesAtom
  );
  const [isLoading, setIsLoading] = useState(true);
  const [externalRefreshCount, setExternalRefreshCount] = useState(0);

  const { hasManageOwnProjectsAccess, hasProjectTeamAccess } = useSelector(
    ({ grants }) => grants
  );

  const planningEntitiesRef = useRef<PlanningEntityMap | null>(
    planningEntities
  );
  const isPlanAppointmentsLoadingRef = useRef<boolean>(false);
  const selectedDateRangeRef = useRef({ startDate, endDate });
  const planUnitIdsRef = useRef<number[]>(getPlanUnitIds(selectedPlanUnits));
  const planningEntityTypeRef = useRef<PlanningEntityType>(planningEntityType);

  const [sessionStoredClientIds] = useSessionStorage<string[]>(
    LISTVIEW_SESSION_STORAGE_KEYS.FILTERED_CLIENTS,
    []
  );

  const { data: travelTimeSettings } = useGetAdminSettings(
    'activity-planning-travel-time'
  );
  const thresholdInMinutes = travelTimeSettings?.[0]?.settings.find(
    (s) => s.key === 'travel.time.show.above.x.minutes'
  )?.value;

  // Legacy `View.Roster` is excluded to preserve prod behaviour (a previous
  // operator-precedence bug meant it never got the threshold); the new
  // Pragmatic Roster intentionally diverges and aligns with the Cards views.
  const cardsViewThreshold =
    view === View.Cards ||
    view === View.CardsPragmatic ||
    view === View.RosterPragmatic
      ? +thresholdInMinutes
      : 0;

  // Hand-written, and has to be: this file cannot carry the React Compiler
  // annotation — it bails with `lowerReorderableExpression` (`LogicalExpression`
  // / `BinaryExpression` cannot be safely reordered), so this is the only
  // memoization available here. (Written without the directive spelling on
  // purpose: `react-compiler:check` greps for it and would scan this file.)
  //
  // Memoized for identity, not for the cost of building it: this array is handed
  // to the board components, so a fresh one every render invalidates their
  // memoization even when the contents are unchanged.
  const currentPlanningEntityIdentifiers = useMemo(
    () =>
      [
        ...Object.values(planningEntities ?? {}).map(
          ({ planningEntityIdentifier }) => planningEntityIdentifier
        ),
        ...(employeeEntityIds ?? []).map((entityId) => ({
          entityId,
          planningEntityType: 'EMPLOYEE' as PlanningEntityType
        })),
        ...(selectedPlanningEntities ? selectedPlanningEntities : []) // Filters etc.
      ].filter((identifier) => identifier),
    [employeeEntityIds, planningEntities, selectedPlanningEntities]
  );

  const planUnitIds =
    !selectedPlanningEntities || selectedPlanningEntities?.length === 0
      ? getPlanUnitIds(selectedPlanUnits)
      : [];
  const planningEntityIdentifiers = getMatchingPlanningEntityIdentifiers(
    currentPlanningEntityIdentifiers,
    planResponsePlanningEntityIdentifiers,
    selectedPlanningEntities,
    planUnitIds
  );

  // Everything the *user* can change to replace the whole board. Two things are
  // deliberately not in here: `planningEntityIdentifiers`, which is derived from
  // `planningEntities` and so moves on every drop (each drop would look like a
  // new board), and the raw plan-unit selection — `planUnitIds` is the effective
  // set the request uses, which is empty while a filter is driving the query, so
  // changing the selection then reloads nothing and must not read as a new board.
  const boardKey = [
    planningEntityType,
    startDate,
    endDate,
    planUnitIds.join(','),
    (employeeEntityIds ?? []).join(','),
    (selectedPlanningEntities ?? [])
      .map(({ planningEntityType: type, entityId }) => `${type}:${entityId}`)
      .join(',')
  ].join('|');

  const isFetchEnabled = useCallback(() => {
    switch (true) {
      // Negaters
      case !startDate:
        return false;

      case planningEntityIdentifiers.length === 0 &&
        planUnitIds.length === 0 &&
        planningEntityType !== 'PROJECT':
        return false;

      // Valid statements
      case planningEntityType === 'PROJECT':
        return true;

      case (hasManageOwnProjectsAccess || hasProjectTeamAccess) &&
        planningEntityType === 'EMPLOYEE' &&
        selectedDateRangeRef.current.startDate === startDate &&
        selectedDateRangeRef.current.endDate === endDate:
        return true;

      case planningEntityType === 'CLIENT':
        if (view === View.List) {
          // UsePlanning should only trigger when there are selected planning entities or when there are session stored client ids
          return (
            planningEntityIdentifiers.length > 0 &&
            (sessionStoredClientIds.length === 0 ||
              (sessionStoredClientIds.length > 0 &&
                planningEntityIdentifiers.some(
                  ({ planningEntityType }) => planningEntityType === 'CLIENT'
                )))
          );
        }
        return selectedPlanUnits?.length > 0;

      case selectedPlanUnits?.length > 0 &&
        selectedDateRangeRef.current.startDate === startDate &&
        selectedDateRangeRef.current.endDate === endDate:
        return true;

      // All else is false
      default:
        return false;
    }
  }, [
    selectedPlanUnits,
    planningEntityType,
    selectedPlanningEntities,
    hasManageOwnProjectsAccess,
    hasProjectTeamAccess,
    view,
    sessionStoredClientIds,
    planningEntityIdentifiers
  ]);

  const {
    data,
    isLoading: queryLoading,
    isFetching,
    isError,
    refetch
  } = useGetPlanning(
    startDate as string,
    endDate as string,
    getPlanningEntityTypeByView(planningEntityType, view),
    planningEntityIdentifiers,
    {
      enabled: isFetchEnabled()
    },
    [planResponseTimestamp]
  );

  useEffect(() => {
    planningEntitiesRef.current = planningEntities;
    selectedDateRangeRef.current = { startDate, endDate };
    planUnitIdsRef.current = getPlanUnitIds(selectedPlanUnits);
    planningEntityTypeRef.current = planningEntityType;
  }, [
    selectedPlanUnits,
    startDate,
    endDate,
    planningEntities,
    planningEntityType
  ]);

  useEffect(() => {
    refetchPlanResponsePlanningEntityIdentifiers(
      setPlanResponsePlanningEntityIdentifiers,
      planResponseTimestamp
    );
  }, [selectedPlanUnits, startDate, endDate]);

  useEffect(() => {
    if (!isFetchEnabled()) setPlanningEntities(null);
  }, [planningEntityType, selectedPlanUnits]);

  useEffect(() => {
    if (data) {
      const normalizedPlanningEntities = normalizePlanningData(
        data,
        cardsViewThreshold
      );

      const isProjectCall = Object.values(
        normalizedPlanningEntities ?? {}
      ).some((entity: PlanningEntity) => entity.type === 'PROJECT');
      const isOrganizationalUnitCall = planningEntityIdentifiers.some(
        ({ planningEntityType }) => planningEntityType === 'ORGANIZATIONAL_UNIT'
      ); // Original request identifiers

      // On a page load (or in case of projects) we use an empty object to clear out any old entities, else we update existing entities
      const replacedPlanningEntities: PlanningEntityMap = {
        ...(isOrganizationalUnitCall || isProjectCall ? {} : planningEntities)
      };
      Object.keys(normalizedPlanningEntities).forEach((key) => {
        replacedPlanningEntities[key] = normalizedPlanningEntities[key];
      });

      apDebug('planningDataMerged', {
        replacedKeys: Object.keys(normalizedPlanningEntities),
        keptKeys: Object.keys(replacedPlanningEntities).filter(
          (key) => !(key in normalizedPlanningEntities)
        ),
        isOrganizationalUnitCall,
        isProjectCall,
        requestIdentifiers: planningEntityIdentifiers
      });
      // `planResponseTimestamp` identifies which fetch this data belongs to —
      // the verification only accepts the drop's own refetch (see
      // `expectDropRefetch`).
      verifyDropAfterRefresh(
        normalizedPlanningEntities,
        'refetch',
        planResponseTimestamp
      );

      setKidsRatio(data.kidsRatio ?? null);
      setPlanningEntities(replacedPlanningEntities);

      const { employees } = getClientsOrEmployeesList(replacedPlanningEntities);

      setEmployees(employees);
      setDataAvailable(true);
    }
  }, [data]);

  useEffect(() => {
    const eventBusUrl = `/activity-planning`;

    if (dataAvailable) {
      if (EventBus.isRegistered(eventBusUrl, onPlanningUpdate)) return;
      EventBus.register(eventBusUrl, onPlanningUpdate);
    }

    return () => {
      if (!EventBus.isRegistered(eventBusUrl, onPlanningUpdate)) return;
      EventBus.unregister(eventBusUrl, onPlanningUpdate);
    };
  }, [dataAvailable]);

  const onPlanningUpdate = async (update) => {
    const { type, body } = update;
    const planningEntities = planningEntitiesRef.current;

    if (type === 'planningEntitiesUpdated' && planningEntities) {
      if (isPlanAppointmentsLoadingRef.current) {
        apDebug('websocket.skipped', {
          type,
          reason: 'plan-appointments call in flight'
        });
        return;
      }

      const b: PlanningEntitiesWebsocketBody = JSON.parse(body);

      const planningEntityIdentifiers = getMatchingPlanningEntityIdentifiers(
        Object.values(planningEntities)
          .filter(Boolean)
          .map(({ planningEntityIdentifier }) => planningEntityIdentifier),
        b.planningEntityIdentifiers,
        selectedPlanningEntities,
        planUnitIdsRef.current
      );

      if (
        isEqualToSessionCorrelationToken(b.correlationToken) ||
        planningEntityIdentifiers.length === 0
      ) {
        apDebug('websocket.skipped', {
          type,
          reason: isEqualToSessionCorrelationToken(b.correlationToken)
            ? 'own correlation token'
            : 'no matching planning entities',
          eventIdentifiers: b.planningEntityIdentifiers
        });
        return;
      }

      apDebug('websocket.applied', {
        type,
        matchedIdentifiers: planningEntityIdentifiers
      });

      const planningEntityMap = await getPlanningByResponseEntities({
        startDate: selectedDateRangeRef.current.startDate as string,
        endDate: selectedDateRangeRef.current.endDate as string,
        organizationalUnitPlanningEntityPerspective:
          getPlanningEntityTypeByView(planningEntityTypeRef.current, view),
        planningEntityIdentifiers,
        planningEntities,
        threshold: cardsViewThreshold
      });

      setPlanningEntities(planningEntityMap);

      const { employees } = getClientsOrEmployeesList(planningEntityMap);
      setEmployees(employees);

      return;
    }

    if (type === 'activityPlanningUpdated') {
      if (isPlanAppointmentsLoadingRef.current) {
        apDebug('websocket.skipped', {
          type,
          reason: 'plan-appointments call in flight'
        });
        return;
      }

      const b: WebsocketBody = JSON.parse(body);
      const selectedPlanUnitIds = getPlanUnitIds(selectedPlanUnits);

      if (
        b.date >= (startDate as string) &&
        selectedPlanUnitIds.some((id) => b.organizationalUnitIds.includes(id))
      ) {
        apDebug('websocket.applied', { type, refetch: true, date: b.date });
        // Announced before the refetch, so a consumer sees the reload coming in
        // the same batch as the `isFetching` it raises.
        setExternalRefreshCount((previous) => previous + 1);
        refetch();
      } else {
        apDebug('websocket.skipped', {
          type,
          reason: 'outside date range or plan units',
          date: b.date,
          organizationalUnitIds: b.organizationalUnitIds
        });
      }
      return;
    }
  };

  const {
    mutate: planAppointments,
    mutateAsync: planAppointmentsAsync,
    isLoading: planAppointmentsLoading
  } = mutations.usePlanAppointments();

  useEffect(() => {
    isPlanAppointmentsLoadingRef.current = planAppointmentsLoading;
  }, [planAppointmentsLoading]);

  const {
    mutate: connectAppointmentGrouping,
    isLoading: connectAppointmentLoading
  } = useConnectAppointmentGrouping({
    // TODO: note, remove when connectAppointmentGrouping triggers a websocket event returning a correlationToken
    onSuccess: () => {
      setPlanResponsePlanningEntityIdentifiers({
        ts: Date.now(),
        planningEntityIdentifiers: []
      });
    }
  });

  const { mutate: connectSchedule, isLoading: connectScheduleLoading } =
    useConnectSchedule({
      onSuccess: () => {
        setPlanResponsePlanningEntityIdentifiers({
          ts: Date.now(),
          planningEntityIdentifiers: []
        });
      }
    });

  useEffect(() => {
    if (
      !isError &&
      (isFetching ||
        queryLoading ||
        planAppointmentsLoading ||
        connectAppointmentLoading ||
        connectScheduleLoading)
    ) {
      // Show the loader when data is fetching
      setIsLoading(true);
    } else if (
      !isFetching &&
      !planAppointmentsLoading &&
      !connectAppointmentLoading &&
      !connectScheduleLoading
    ) {
      // Delay hiding the loader until after the next repaint / wait till react has updated the ui
      requestAnimationFrame(() => {
        setIsLoading(false);
      });
    } else {
      // Show the loader when data is fetching
      setIsLoading(true);
    }
  }, [
    isFetching,
    planAppointmentsLoading,
    connectAppointmentLoading,
    connectScheduleLoading,
    queryLoading
  ]);

  return {
    data,
    kidsRatio,
    planningEntities: planningEntities,
    setPlanningEntities: setPlanningEntities,
    planAppointments,
    planAppointmentsAsync,
    connectAppointmentGrouping,
    connectSchedule,
    startDate: startDate as string,
    endDate: endDate as string,
    currentPlanningEntityIdentifiers,
    boardKey,
    externalRefreshCount,
    isLoading,
    employees,
    showEvents
  };
}
